import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  getAdrenalin, schvalitAdrenalin, skrytAdrenalin, skrytAdrenalinOhyb,
  nastavCenuAdrenalin, type AdrenalinDetail,
} from "../lib/api.js";
import { LoadingBlock } from "../ui/Spinner.js";
import { Button } from "../ui/Button.js";
import { Badge } from "../ui/Badge.js";
import { AlertIcon } from "../lib/icons.js";

/**
 * Detail jedné karty ADRENALIN-OIL.
 *
 * 💰 Rozpis ceny je tu JINÝ než u AZ PNEU. Pneumatikám cenu POČÍTÁME
 * (nákupka + doprava + marže), tady ji ZRCADLÍME — Vít 18. 8. 2026:
 * *„nás zajímá prodejní cena, ať sedí s tím, co tam má dodavatel."*
 * ⛔ Proto se tu nesmí objevit vymyšlená nákupka ani marže: nákupku máme
 * jen u karet, které jsou v ABŘE, a párovací kód tam u většiny ještě chybí
 * (řeší Vít s Markem). Co neznáme, se NEUKAZUJE, nedopočítává.
 *
 * 🔧 Řídítka: varianta = OHYB. Rozměry A/B/C/D jsou z PDF katalogu dodavatele
 * — jinde je nemá nikdo, protože ta PDF jsou obrázková.
 */

const kc = (v: string | null): string =>
  v === null ? "—" : `${Math.round(Number(v)).toLocaleString("cs-CZ")} Kč`;

type Tone = "neutral" | "orange" | "green" | "amber" | "red" | "blue";

const STAV: Record<AdrenalinDetail["eshopStav"], { text: string; tone: Tone }> = {
  "na-eshopu": { text: "Na eshopu", tone: "green" },
  ceka: { text: "Čeká na robota", tone: "amber" },
  chyba: { text: "Chyba", tone: "red" },
  skryto: { text: "Skryto", tone: "neutral" },
  mimo: { text: "Není na eshopu", tone: "neutral" },
};

export function AdrenalinDetailPage(): JSX.Element {
  const { supplierId } = useParams<{ supplierId: string }>();
  const [d, setD] = useState<AdrenalinDetail | null>(null);
  const [chyba, setChyba] = useState<string | null>(null);
  const [pracuje, setPracuje] = useState(false);
  const [fotka, setFotka] = useState(0);

  const nacti = useCallback(async () => {
    if (!supplierId) return;
    try {
      setD(await getAdrenalin(supplierId));
    } catch (e) {
      setChyba(e instanceof Error ? e.message : String(e));
    }
  }, [supplierId]);

  useEffect(() => { void nacti(); }, [nacti]);

  const akce = async (fn: () => Promise<void>) => {
    setPracuje(true); setChyba(null);
    try { await fn(); await nacti(); }
    catch (e) { setChyba(e instanceof Error ? e.message : String(e)); }
    finally { setPracuje(false); }
  };

  const prepisCenu = () => {
    if (!d) return;
    const zadano = window.prompt(
      `Naše cena pro „${d.nazev}" (Kč s DPH).\n` +
        `Dodavatel prodává za ${kc(d.cenaDodavatele)}. Prázdné = vrátit se k jeho ceně.`,
      d.cena ? String(Math.round(Number(d.cena))) : "",
    );
    if (zadano === null) return;
    void akce(() => nastavCenuAdrenalin(d.supplierId,
      zadano.trim() === "" ? null : Number(zadano)));
  };

  if (d === null) return chyba ? <div className="text-red-600">{chyba}</div> : <LoadingBlock />;

  const s = STAV[d.eshopStav];
  // Jak jsme na tom proti dodavateli. ⚠ Hlídač: být nad jeho pultovkou je vada —
  // prodává i koncovým zákazníkům a zákazník si to vygoogluje.
  const odchylka = d.cena && d.cenaDodavatele
    ? Number(d.cena) / Number(d.cenaDodavatele) - 1 : null;

  return (
    <div className="p-6 space-y-6">
      <nav className="text-sm text-zinc-500">
        <Link to="/adrenalin" className="hover:underline">ADRENALIN-OIL</Link>
        <span className="mx-1.5">›</span>
        <span>{d.znacka}</span>
        <span className="mx-1.5">›</span>
        <span className="text-zinc-700">{d.druh === "riditka" ? "řídítka" : "filtr"}</span>
      </nav>

      <div className="grid gap-8 lg:grid-cols-2">
        <div>
          <div className="rounded-2xl border border-zinc-200 bg-zinc-50 aspect-square grid place-items-center overflow-hidden">
            {d.fotky[fotka]
              ? <img src={d.fotky[fotka]} alt={d.nazev} className="w-full h-full object-contain p-8" />
              : <span className="text-zinc-300">bez fotky</span>}
          </div>
          {d.fotky.length > 1 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {d.fotky.map((f, i) => (
                <button key={f} type="button" onClick={() => setFotka(i)}
                  className={`h-14 w-14 rounded-lg border overflow-hidden ${
                    i === fotka ? "border-ktm-orange" : "border-zinc-200"}`}>
                  <img src={f} alt="" className="h-full w-full object-contain p-1" />
                </button>
              ))}
            </div>
          )}
          <p className="mt-2 text-[11px] text-zinc-400">
            Fotky jsou od dodavatele. Na eshop jdou všechny, které tu vidíš.
          </p>
        </div>

        <div>
          <div className="flex flex-wrap gap-2 mb-3">
            <Badge tone={s.tone}>{s.text}</Badge>
            <Badge tone="neutral">{d.druh === "riditka" ? "řídítka" : "vzduchový filtr"}</Badge>
            {d.ktm && <Badge tone="neutral">KTM</Badge>}
          </div>

          <h1 className="text-2xl font-semibold">{d.nazev}</h1>
          <p className="mt-1 text-sm text-zinc-500">
            {d.znacka} · kód výrobce <strong>{d.kod ?? "—"}</strong>
            {d.ohyby.length > 0 && ` · ${d.ohyby.length} ohybů`}
          </p>

          {/* 💰 Rozpis ceny — zrcadlíme, nepočítáme */}
          <div className="mt-5 rounded-xl border border-zinc-200 p-4">
            <div className="text-xs text-zinc-500 mb-2">Z čeho vyšla cena</div>
            <table className="w-full text-sm">
              <tbody>
                <tr>
                  <td className="py-1 text-zinc-600">pultovní cena dodavatele</td>
                  <td className="py-1 text-right tabular-nums">{kc(d.cenaDodavatele)}</td>
                </tr>
                <tr className="border-t border-zinc-200">
                  <td className="py-2 font-semibold">= naše cena</td>
                  <td className="py-2 text-right tabular-nums text-lg font-semibold">
                    {kc(d.cena)}
                    {d.cenaPrepsana && (
                      <span className="ml-1 text-amber-600" title="ručně přepsáno">✎</span>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>

            {d.cenaPrepsana && (
              <div className="mt-2 text-xs text-amber-700">
                Cenu ručně přepsal {d.cenaPrepsanaKym ?? "někdo"}
                {d.cenaPrepsanaKdy && ` (${new Date(d.cenaPrepsanaKdy).toLocaleDateString("cs-CZ")})`}
                {" "}— denní robot ji nepřepíše.
              </div>
            )}

            {odchylka !== null && Math.abs(odchylka) > 0.001 && (
              <div className={`mt-3 flex items-start gap-1.5 rounded-lg p-2 text-xs ${
                odchylka > 0 ? "bg-amber-50 text-amber-800" : "bg-zinc-50 text-zinc-600"}`}>
                {odchylka > 0 && <AlertIcon size={14} className="mt-0.5 shrink-0" />}
                <span>
                  Jsme {odchylka > 0 ? "+" : ""}{Math.round(odchylka * 100)} % proti dodavateli.
                  {odchylka > 0 && (
                    <> <strong>Dodavatel prodává i koncovým zákazníkům</strong> — být nad
                    jeho cenou znamená, že si to zákazník vygoogluje levněji.</>
                  )}
                </span>
              </div>
            )}

            <div className="mt-3 flex flex-wrap items-center gap-3">
              <button type="button" onClick={prepisCenu} disabled={pracuje}
                      className="text-sm text-ktm-orange hover:underline">
                Přepsat cenu ručně
              </button>
              {d.supplierUrl && (
                <a href={d.supplierUrl} target="_blank" rel="noreferrer"
                   className="text-sm text-ktm-orange hover:underline">
                  Otevřít u dodavatele →
                </a>
              )}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {d.eshopStav === "na-eshopu" || d.eshopStav === "ceka" ? (
              <Button variant="secondary" disabled={pracuje}
                      onClick={() => void akce(() => skrytAdrenalin(d.supplierId))}>
                Skrýt z eshopu
              </Button>
            ) : (
              <Button disabled={pracuje}
                      onClick={() => void akce(() => schvalitAdrenalin(d.supplierId))}>
                Pustit na eshop
              </Button>
            )}
            <span className="self-center text-xs text-zinc-500">
              {d.eshopStav === "mimo"
                ? "Robot ho založí do 15 minut."
                : "Skrytí se projeví hned."}
            </span>
          </div>

          {chyba && (
            <div className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">{chyba}</div>
          )}
        </div>
      </div>

      {/* Ohyby = varianty */}
      {d.ohyby.length > 0 && (
        <div>
          <h2 className="mt-2 mb-1 text-lg font-semibold">Ohyby ({d.ohyby.length})</h2>
          <p className="mb-3 text-sm text-zinc-500">
            Každý ohyb je vlastní varianta se svým skladem. Rozměry jsou z katalogu
            výrobce: <strong>A</strong> šířka, <strong>B</strong> výška,
            {" "}<strong>C</strong> drop, <strong>D</strong> rozteč (mm).
          </p>
          <div className="overflow-x-auto rounded-xl border border-zinc-200">
            <table className="w-full min-w-[40rem] text-sm">
              <thead className="border-b border-zinc-200 bg-zinc-50 text-left text-xs uppercase text-zinc-500">
                <tr>
                  <th className="px-3 py-2">Ohyb</th>
                  <th className="px-3 py-2">Objednací kód</th>
                  <th className="px-3 py-2 text-right">A</th>
                  <th className="px-3 py-2 text-right">B</th>
                  <th className="px-3 py-2 text-right">C</th>
                  <th className="px-3 py-2 text-right">D</th>
                  <th className="px-3 py-2 text-right">Sklad u nich</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {d.ohyby.map((o) => (
                  <tr key={o.kod}>
                    <td className="px-3 py-2 font-medium">{o.ohyb}</td>
                    <td className="px-3 py-2 font-mono text-xs text-zinc-500">{o.kod}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{o.sirka ?? "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{o.vyska ?? "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{o.drop ?? "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{o.roztec ?? "—"}</td>
                    <td className={`px-3 py-2 text-right ${
                      o.ks > 0 ? "text-green-700" : "text-red-600"}`}>
                      {o.ks > 0 ? `${o.ks} ks` : "nemá"}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {d.ohyby.length > 1 && d.eshopStav === "na-eshopu" && (
                        <button type="button" disabled={pracuje}
                          onClick={() => void akce(() => skrytAdrenalinOhyb(d.supplierId, o.kod))}
                          className="text-xs text-zinc-500 hover:text-red-600 hover:underline">
                          Skrýt
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Fitment od výrobce */}
      {d.fitmentModely.length > 0 && (
        <div>
          <h2 className="mt-2 mb-1 text-lg font-semibold">
            Sedne na <span className="text-sm font-normal text-zinc-400">
              podle výrobce · {d.fitmentModely.length} modelů
            </span>
          </h2>
          <p className="mb-2 text-sm text-zinc-500">
            Fitment je od <strong>FunnelWeb Filter</strong>, ne z popisu dodavatele —
            ten je špatně u 5 z 13 filtrů.
            {d.fitmentRoky.length > 0 &&
              ` Ročníky ${d.fitmentRoky[0]}–${d.fitmentRoky[d.fitmentRoky.length - 1]}.`}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {d.fitmentModely.map((m) => (
              <span key={m} className="rounded-md bg-zinc-100 px-2 py-0.5 text-xs text-zinc-700">
                {m}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Popis od dodavatele */}
      {(d.perexHtml || d.descriptionHtml) && (
        <div>
          <h2 className="mt-2 mb-1 text-lg font-semibold">
            Popis <span className="text-sm font-normal text-zinc-400">od dodavatele</span>
          </h2>
          <p className="mb-2 text-[11px] text-zinc-400">
            Na eshop jde po úpravě — tohle je originál od zdroje.
          </p>
          <div className="prose prose-sm max-w-none text-zinc-700">
            {d.perexHtml && (
              <div className="font-medium" dangerouslySetInnerHTML={{ __html: d.perexHtml }} />
            )}
            {d.descriptionHtml && (
              <div dangerouslySetInnerHTML={{ __html: d.descriptionHtml }} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
